
import React, { useState } from 'react';
import { ArrowLeftRight, Search, Filter, Download, ArrowUpRight, ArrowDownLeft, RefreshCw, MoreVertical, CheckCircle2, Clock, XCircle, TrendingUp, Briefcase, Users } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { formatDisplayName, maskString } from '@/shared/utils/formatters';

const AdminTransactionsPage: React.FC = () => {
    const [filter, setFilter] = useState('All');
    const [transactions, setTransactions] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [isSettling, setIsSettling] = useState(false);

    // Fetch transactions from Firestore
    React.useEffect(() => {
        // Use 'timestamp' if possible, fallback to 'createdAt' if sorting fails or for legacy data
        const q = query(collection(db, 'transactions'), orderBy('timestamp', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const txData = snapshot.docs.map(doc => {
                const data = doc.data();
                // Find the best date field (timestamp or createdAt)
                const dateObj = data.timestamp || data.createdAt;
                return {
                    id: doc.id,
                    ...data,
                    date: dateObj?.toDate?.().toLocaleString() || 'N/A'
                };
            });
            setTransactions(txData);
            setLoading(false);
        }, (error) => {
            console.error("Firestore sort error (trying legacy createdAt):", error);
            // Fallback for indexes that might only have createdAt
            const qFallback = query(collection(db, 'transactions'), orderBy('createdAt', 'desc'));
            onSnapshot(qFallback, (snapshot) => {
                const txData = snapshot.docs.map(doc => {
                    const data = doc.data();
                    const dateObj = data.timestamp || data.createdAt;
                    return {
                        id: doc.id,
                        ...data,
                        date: dateObj?.toDate?.().toLocaleString() || 'N/A'
                    };
                });
                setTransactions(txData);
            });
        });

        return () => unsubscribe();
    }, []);

    const filteredTransactions = transactions.filter(tx => {
        const typeStr = (tx.type as string || '').toUpperCase();
        const assetStr = (tx.asset as string || '').toUpperCase();

        // 입출금 상신 내역은 이 페이지에서 제외 (전용 페이지에서 관리)
        if (typeStr === 'DEPOSIT' || typeStr === 'WITHDRAWAL' || typeStr === 'WITHDRAW') return false;

        if (filter === 'All') return true;

        if (filter === 'Swap' && typeStr === 'SWAP' && !assetStr.includes('P2P')) return true;
        if (filter === 'Day Trading' && (typeStr === 'TRADING_BUY' || typeStr === 'TRADING_SELL')) return true;
        if (filter === 'Block Deal' && (typeStr === 'BLOCK_DEAL' || assetStr.includes('BLOCK DEAL'))) return true;
        if (filter === 'ETF' && (typeStr.includes('ETF') || assetStr.includes('ETF'))) return true;
        if (filter === 'Staking' && (typeStr.includes('STAKING') || typeStr === 'STAKE' || assetStr.includes('STAKING'))) return true;
        if (filter === 'P2P' && (typeStr === 'P2P' || assetStr.includes('P2P'))) return true;

        return false;
    });

    // Calculate real stats
    const stats = {
        volume: filteredTransactions.reduce((acc, tx) => acc + (parseFloat(tx.amount?.toString().replace(/,/g, '') || '0')), 0),
        pending: transactions.filter(tx => tx.type === 'TRADING_BUY' && !tx.settled && tx.status === 'COMPLETED').length,
        successRate: 98.2, // Mock or calculate if needed
        traders: new Set(transactions.map(tx => tx.userId)).size
    };

    const handleManualSettle = async () => {
        const unsettled = transactions.filter(tx => tx.type === 'TRADING_BUY' && !tx.settled && tx.status === 'COMPLETED');
        if (unsettled.length === 0) {
            alert('정산할 내역이 없습니다.');
            return;
        }

        if (!window.confirm(`${unsettled.length}건의 미정산 내역을 강제로 정산하시겠습니까?`)) return;

        setIsSettling(true);
        try {
            const { getDoc, doc, writeBatch, serverTimestamp, increment } = await import('firebase/firestore');

            // 1. Get all round results needed
            const missingRounds = [...new Set(unsettled.map(tx => tx.metadata?.round))].filter(Boolean);
            const results: Record<number, string> = {};

            for (const r of missingRounds) {
                const resDoc = await getDoc(doc(db, 'round_results', `BTC_${r}`));
                if (resDoc.exists()) results[r as number] = resDoc.data().result;
            }

            const batch = writeBatch(db);
            let winCount = 0;

            for (const tx of unsettled) {
                const round = tx.metadata?.round;
                const result = results[round];
                if (!result) continue;

                const direction = tx.metadata?.direction;
                const isWin = direction === result;

                if (isWin) {
                    const profit = Math.floor(tx.amount * 1.95);
                    batch.update(doc(db, 'users', tx.userId), {
                        krw: increment(profit)
                    });

                    batch.set(doc(collection(db, 'transactions')), {
                        userId: tx.userId,
                        userName: tx.userName || 'User',
                        type: 'TRADING_SELL',
                        amount: profit,
                        asset: 'KRW',
                        status: 'COMPLETED',
                        timestamp: serverTimestamp(),
                        metadata: {
                            description: `Day Trading Manual Win #${round}`,
                            round: round,
                            type: 'WIN'
                        }
                    });
                    winCount++;
                }

                batch.update(doc(db, 'transactions', tx.id), {
                    settled: true,
                    settledAt: serverTimestamp(),
                    outcome: isWin ? 'WIN' : 'LOSS'
                });
            }

            await batch.commit();
            alert(`정산 완료: 총 ${unsettled.length}건 처리 (${winCount}건 적중)`);
        } catch (error) {
            console.error("Manual settlement failed", error);
            alert("정산 처리 중 오류가 발생했습니다.");
        } finally {
            setIsSettling(false);
        }
    };

    const getStatusStyle = (status: string) => {
        const s = (status || '').toUpperCase();
        switch (s) {
            case 'COMPLETED':
            case 'SUCCESS':
            case 'APPROVED':
                return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
            case 'PENDING':
                return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
            case 'FAILED':
            case 'CANCELLED':
            case 'REJECTED':
                return 'bg-red-500/10 text-red-500 border-red-500/20';
            default:
                return 'bg-slate-500/10 text-slate-500 border-slate-500/20';
        }
    };

    const getTypeIcon = (tx: any) => {
        const typeStr = (tx.type as string || '').toUpperCase();
        const assetStr = (tx.asset as string || '').toUpperCase();

        if (typeStr.includes('ETF')) return <TrendingUp className="h-3.5 w-3.5" />;
        if (typeStr.includes('STAKING') || typeStr.includes('STAKE') || assetStr.includes('STAKING')) return <TrendingUp className="h-3.5 w-3.5 rotate-45" />;
        if (typeStr.includes('BLOCK')) return <Briefcase className="h-3.5 w-3.5" />;
        if (typeStr === 'P2P' || assetStr.includes('P2P')) return <Users className="h-3.5 w-3.5" />;
        return <ArrowLeftRight className="h-3.5 w-3.5" />;
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-700">
            {/* Header omitted for brevity in preview, assumed existing */}
            <div className="flex items-center justify-between">
                <div className="space-y-1">
                    <h1 className="text-2xl font-black flex items-center gap-3">
                        <ArrowLeftRight className="h-7 w-7 text-blue-500" /> 시스템 거래 내역
                    </h1>
                    <p className="text-slate-500 font-bold text-sm uppercase tracking-widest">글로벌 거래 모니터링</p>
                </div>
                <div className="flex items-center gap-4">
                    <button
                        onClick={handleManualSettle}
                        disabled={isSettling}
                        className={cn(
                            "px-6 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-all",
                            isSettling ? "bg-slate-800 text-slate-500" : "bg-amber-500 hover:bg-amber-400 text-black shadow-lg shadow-amber-500/20"
                        )}
                    >
                        {isSettling ? '정산 처리 중...' : `미정산 ${stats.pending}건 강제 정산`}
                    </button>
                    <div className="w-12 h-12 rounded-full border border-slate-800 flex items-center justify-center bg-white/5">
                        <ArrowUpRight className="h-5 w-5 text-slate-500" />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="p-6 rounded-[2rem] bg-slate-950 border border-slate-800 space-y-2">
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">금일 거래량</p>
                    <div className="flex items-end justify-between">
                        <h4 className="text-xl font-black text-white">₩{stats.volume.toLocaleString()}</h4>
                        <span className="text-[10px] font-black px-1.5 py-0.5 rounded text-blue-500 bg-white/5">+12.4%</span>
                    </div>
                </div>
                <div className="p-6 rounded-[2rem] bg-slate-950 border border-slate-800 space-y-2">
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">지급 대기</p>
                    <div className="flex items-end justify-between">
                        <h4 className="text-xl font-black text-white">{stats.pending}건</h4>
                        <span className="text-[10px] font-black px-1.5 py-0.5 rounded text-amber-500 bg-white/5">Manual Rev</span>
                    </div>
                </div>
                <div className="p-6 rounded-[2rem] bg-slate-950 border border-slate-800 space-y-2">
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">성공률</p>
                    <div className="flex items-end justify-between">
                        <h4 className="text-xl font-black text-white">{stats.successRate}%</h4>
                        <span className="text-[10px] font-black px-1.5 py-0.5 rounded text-emerald-500 bg-white/5">Steady</span>
                    </div>
                </div>
                <div className="p-6 rounded-[2rem] bg-slate-950 border border-slate-800 space-y-2">
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">활성 트레이더</p>
                    <div className="flex items-end justify-between">
                        <h4 className="text-xl font-black text-white">{stats.traders}명</h4>
                        <span className="text-[10px] font-black px-1.5 py-0.5 rounded text-indigo-500 bg-white/5">+8</span>
                    </div>
                </div>
            </div>

            <div className="rounded-[2.5rem] bg-slate-950 border border-slate-800 overflow-hidden shadow-2xl">
                <div className="px-8 py-6 border-b border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-6 bg-white/[0.02]">
                    <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0 scrollbar-hide">
                        {['All', 'Swap', 'Day Trading', 'Block Deal', 'ETF', 'Staking', 'P2P'].map((t) => (
                            <button
                                key={t}
                                onClick={() => setFilter(t)}
                                className={cn(
                                    "px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap",
                                    filter === t ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20" : "text-slate-500 hover:text-white"
                                )}
                            >
                                {t === 'All' ? '전체' :
                                    t === 'Swap' ? '스왑' :
                                        t === 'Day Trading' ? '데이 트레이딩' :
                                            t === 'Block Deal' ? '블록딜' :
                                                t === 'Staking' ? '스테이킹' : t}
                            </button>
                        ))}
                    </div>
                    {/* Search Field preserved */}
                    <div className="relative min-w-[240px]">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                        <input type="text" placeholder="사용자 또는 ID 검색..." className="w-full h-11 bg-slate-900 border border-slate-800 rounded-xl pl-11 pr-4 text-xs font-bold outline-none focus:border-blue-500/50" />
                    </div>
                </div>

                <table className="w-full text-xs">
                    <thead className="bg-[#0f0f12] text-slate-500 font-black uppercase tracking-widest border-b border-slate-800">
                        <tr>
                            <th className="px-8 py-5 text-left">사용자</th>
                            <th className="px-8 py-5 text-left">유형</th>
                            <th className="px-8 py-5 text-left">자산</th>
                            <th className="px-8 py-5 text-right">금액</th>
                            <th className="px-8 py-5 text-center">상태</th>
                            <th className="px-8 py-5 text-right">일시</th>
                            <th className="px-8 py-5 text-right">관리</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800 font-medium">
                        {loading ? (
                            <tr>
                                <td colSpan={7} className="px-8 py-10 text-center text-slate-500">거래 내역 로딩중...</td>
                            </tr>
                        ) : filteredTransactions.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="px-8 py-10 text-center text-slate-500">거래 내역이 없습니다.</td>
                            </tr>
                        ) : (
                            filteredTransactions.map((tx) => (
                                <tr key={tx.id} className="hover:bg-white/[0.01] transition-colors group">
                                    <td className="px-8 py-5">
                                        <div className="flex flex-col">
                                            <span className="font-bold text-white group-hover:text-blue-400 transition-colors uppercase tracking-tight">
                                                {formatDisplayName({ userName: tx.userName, id: tx.userId })}
                                            </span>
                                            <span className="text-[10px] text-slate-600 font-mono truncate max-w-[120px]">{maskString(tx.userId)}</span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5">
                                        <div className="flex items-center gap-2">
                                            <div className={cn(
                                                "w-6 h-6 rounded-lg flex items-center justify-center bg-blue-500/10 text-blue-500",
                                                tx.type === 'TRADING_BUY' && "bg-red-500/10 text-red-500",
                                                tx.type === 'TRADING_SELL' && "bg-blue-500/10 text-blue-500"
                                            )}>
                                                {getTypeIcon(tx)}
                                            </div>
                                            <span className={cn(
                                                "font-bold uppercase text-[10px]",
                                                tx.type === 'TRADING_BUY' ? "text-red-500" :
                                                    tx.type === 'TRADING_SELL' ? "text-blue-500" : "text-slate-400"
                                            )}>
                                                {tx.type === 'TRADING_BUY' ? '매수' :
                                                    tx.type === 'TRADING_SELL' ? '매도' : tx.type}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5 text-slate-300 uppercase font-black text-[10px] tracking-tighter">{tx.asset}</td>
                                    <td className="px-8 py-5 text-right font-black text-white">{tx.amount}</td>
                                    <td className="px-8 py-5">
                                        <div className="flex justify-center">
                                            <div className={cn("inline-flex items-center gap-1.5 px-3 py-1 rounded-lg border text-[9px] font-black uppercase tracking-widest", getStatusStyle(tx.status))}>
                                                {getStatusStyle(tx.status).includes('emerald') && <CheckCircle2 className="h-3.5 w-3.5" />}
                                                {getStatusStyle(tx.status).includes('amber') && <Clock className="h-3.5 w-3.5" />}
                                                {getStatusStyle(tx.status).includes('red') && <XCircle className="h-3.5 w-3.5" />}
                                                {tx.status}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5 text-right text-slate-500 font-mono text-[10px]">{tx.date}</td>
                                    <td className="px-8 py-5 text-right">
                                        <button className="p-2 rounded-lg bg-slate-900 border border-slate-800 text-slate-500 hover:text-white transition-colors">
                                            <MoreVertical className="h-4 w-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>

                <div className="p-8 border-t border-slate-800 bg-white/[0.01] flex items-center justify-between">
                    <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">{filteredTransactions.length}개 항목 표시 중</p>
                    <div className="flex items-center gap-2">
                        <button className="h-10 px-4 rounded-xl bg-slate-900 border border-slate-800 text-slate-500 text-[10px] font-black uppercase disabled:opacity-50" disabled>이전</button>
                        <button className="h-10 px-4 rounded-xl bg-blue-600 text-white text-[10px] font-black uppercase shadow-lg shadow-blue-600/20">다음</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminTransactionsPage;
